import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Toast } from "shared-ui/src/components/Toast";
import { invalidateCart } from "@features/ecommerce/cart/cartQueryKeys";
import { useApi } from "@utils/useApi";
import {
  ApiException,
  BuyAgainItemDto,
  BuyAgainRequestDto,
  EcommerceShoppingCartClient,
  OrderLineItemDto,
} from "@lib/ShiOneClient";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";

const useBuyAgainOrder = () => {
  const queryClient = useQueryClient();
  const api = useApi(EcommerceShoppingCartClient);
  const { hasPermission } = useClaims();

  const buyAgain = async (items: OrderLineItemDto[]): Promise<BuyAgainResponse> => {
    if (!hasPermission(ShiOnePermission.shoppingCartSave)) {
      throw new Error("Access denied: Shopping cart save permission required.");
    }
    if (!items.length) {
      throw new Error("No items available to buy again.");
    }

    const request = new BuyAgainRequestDto();
    const filteredItems = items.filter(
      (item) => item.productDescriptor || item.shiPartNumber,
    );
    if (!filteredItems.length) {
      throw new Error("No items with a valid product identifier to buy again.");
    }
    request.items = filteredItems
      .map(
        (item) =>
          new BuyAgainItemDto({
            productDescriptor: item.productDescriptor || String(item.shiPartNumber || ""),
            productClass: item.productClass || "shicommodity",
            quantity: item.quantity ?? 1,
            productName: item.productName || item.productDescription || item.mfgPartNumber || "",
            shiPartNumber: item.shiPartNumber != null ? String(item.shiPartNumber) : undefined,
          }),
      );

    const result = await api.buyAgain(request);

    return {
      successCount: result.successCount ?? 0,
      failedCount: result.failedCount ?? 0,
      failedItems:
        result.failedItems?.map((f) => ({
          productDescriptor: f.productDescriptor ?? "",
          productName: f.productName ?? "",
          reason: f.reason ?? "",
        })) ?? [],
    };
  };

  const mutation = useMutation<BuyAgainResponse, Error, OrderLineItemDto[]>({
    mutationFn: buyAgain,
    onSuccess: async (result) => {
      const addedLabel = `${result.successCount} ${result.successCount === 1 ? "item" : "items"}`;
      const failedLabel = `${result.failedCount} ${result.failedCount === 1 ? "item" : "items"}`;

      if (result.successCount > 0 && result.failedCount > 0) {
        Toast(
          `Added ${addedLabel} to cart. ${failedLabel} could not be added.`,
          "warning",
        );
      } else if (result.successCount > 0) {
        Toast(`Added ${addedLabel} to cart`);
      } else {
        Toast("No items were added to cart.", "warning");
      }
      await invalidateCart(queryClient);
    },
    onError: (error) => {
      const status = ApiException.isApiException(error) ? error.status : undefined;
      const message = status === 401 || status === 403
        ? "Not authorized to add items to cart."
        : status === 502 || status === 503
          ? "Shopping cart service is unavailable. Please try again."
          : `Buy Again failed${status ? ` (${status})` : ""}: ${error?.message}`;
      Toast(message, true, 7000);
    },
  });

  return {
    ...mutation,
    clearBuyAgainResult: () => mutation.reset(),
  };
};

export type BuyAgainResponse = {
  successCount: number;
  failedCount: number;
  failedItems: { productDescriptor: string; productName: string; reason: string }[];
};

export default useBuyAgainOrder;
